import type { Participant } from '@seen/core/models/patients/participant'
import {
  ATTENDANCE_SHIFTS,
  ATTENDANCE_WEEKDAYS,
  type AttendanceShiftsByDay,
  type AttendanceWeekday,
  DayCenterSchedule,
  type ShiftMode,
  shiftModeFor,
} from '@seen/core/models/schedules/dayCenterSchedule'
import {
  Button,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@seen/ui'
import { pick } from 'lodash-es'
import { type JSX, useEffect } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Textarea } from '#src/components/core/Textarea.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { SegmentedControl } from '#src/components/patient/detail-content/day-center-schedule/SegmentedControl.tsx'
import { useDayCenterScheduleActivity } from '#src/components/patient/detail-content/day-center-schedule/UseDayCenterScheduleActivity.tsx'
import {
  dayCenterDaysSummary,
  preferredDaysSummary,
  SHIFT_MODE_OPTIONS,
  SHIFT_OPTIONS,
  WEEKDAY_LETTER_LABELS,
  WEEKDAY_LONG_LABELS,
} from '#src/components/patient/detail-content/day-center-schedule/weekdays.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

export interface DayCenterScheduleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  participant: Participant
  schedule: DayCenterSchedule | null
}

const shiftsSchema = z.record(
  z.enum(ATTENDANCE_WEEKDAYS),
  z.enum(ATTENDANCE_SHIFTS)
)

// react-hook-form / useForm disallow `undefined` field types, so use nullable
// (not optional) for the note and the shift modes.
const formSchema = z.object({
  days: z.array(z.enum(ATTENDANCE_WEEKDAYS)),
  shiftMode: z.enum(['am', 'pm', 'custom']).nullable(),
  shifts: shiftsSchema,
  preferredDays: z.array(z.enum(ATTENDANCE_WEEKDAYS)),
  preferredShiftMode: z.enum(['am', 'pm', 'custom']).nullable(),
  preferredShifts: shiftsSchema,
  note: z.string().nullable(),
})

// A uniform mode owns every day in the set; `custom` keeps whatever each day
// already had, dropping days that left the set.
function shiftsForMode(
  mode: ShiftMode | null,
  days: AttendanceWeekday[],
  current: AttendanceShiftsByDay
): AttendanceShiftsByDay {
  if (!mode) {
    return {}
  }
  if (mode === 'custom') {
    return pick(current, days)
  }
  return Object.fromEntries(days.map((day) => [day, mode]))
}

export function DayCenterScheduleDialog({
  open,
  onOpenChange,
  participant,
  schedule,
}: DayCenterScheduleDialogProps): JSX.Element {
  const isReadOnly = useIsReadOnly()

  // Only fetch version history while the dialog is open.
  const activity = useDayCenterScheduleActivity(open ? schedule?.id : undefined)

  const scheduleDays = schedule?.attendanceDays ?? []
  const scheduleDaysKey = scheduleDays.join('|')
  const scheduleNote = schedule?.note ?? ''

  const form = useForm({
    schema: formSchema,
    defaultValues: {
      days: scheduleDays,
      shiftMode:
        shiftModeFor(schedule?.attendanceShifts ?? {}, scheduleDays) ?? null,
      shifts: schedule?.attendanceShifts ?? {},
      preferredDays: schedule?.preferredAppointmentDays ?? [],
      preferredShiftMode:
        shiftModeFor(
          schedule?.preferredAppointmentShifts ?? {},
          schedule?.preferredAppointmentDays ?? []
        ) ?? null,
      preferredShifts: schedule?.preferredAppointmentShifts ?? {},
      note: scheduleNote,
    },
  })
  const { isDirty } = useFormState({ control: form.control })
  const { reset, setValue, control } = form
  const values = useWatch({ control })

  const days = values.days ?? []
  const shifts = values.shifts ?? {}
  const shiftMode = values.shiftMode ?? null
  const preferredDays = values.preferredDays ?? []
  const preferredShifts = values.preferredShifts ?? {}
  const preferredShiftMode = values.preferredShiftMode ?? null

  // Reset only on dialog open or schedule identity changes; background refetches
  // for the same schedule must not discard in-progress edits.
  useEffect(() => {
    if (open) {
      const selectedDays = new Set(scheduleDaysKey.split('|'))
      const nextDays = ATTENDANCE_WEEKDAYS.filter((day) =>
        selectedDays.has(day)
      )
      const nextShifts = schedule?.attendanceShifts ?? {}
      const nextPreferredDays = schedule?.preferredAppointmentDays ?? []
      const nextPreferredShifts = schedule?.preferredAppointmentShifts ?? {}
      reset({
        days: nextDays,
        shiftMode: shiftModeFor(nextShifts, nextDays) ?? null,
        shifts: nextShifts,
        preferredDays: nextPreferredDays,
        preferredShiftMode:
          shiftModeFor(nextPreferredShifts, nextPreferredDays) ?? null,
        preferredShifts: nextPreferredShifts,
        note: scheduleNote,
      })
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- content-derived values intentionally do not trigger resets
  }, [open, reset, schedule?.id])

  const patchMutation = usePatchMutation<DayCenterSchedule>()
  const createMutation = useCreateMutation<DayCenterSchedule>({
    disableDefaultErrorToast: true,
  })
  const isSaving = patchMutation.isPending || createMutation.isPending

  const dirty = { shouldDirty: true } as const

  // Day-center days drive only their own shifts: a day that stops being
  // attended can no longer carry a day-center shift. External appointment
  // preferences are chosen independently, so they are left untouched here.
  const setDays = (nextDays: AttendanceWeekday[]): void => {
    setValue('days', nextDays, dirty)
    setValue('shifts', shiftsForMode(shiftMode, nextDays, shifts), dirty)
  }

  const setPreferredDays = (nextDays: AttendanceWeekday[]): void => {
    setValue('preferredDays', nextDays, dirty)
    setValue(
      'preferredShifts',
      shiftsForMode(preferredShiftMode, nextDays, preferredShifts),
      dirty
    )
  }

  const submit = form.handleSubmit((formValues) => {
    const onSuccess = (): void => {
      onOpenChange(false)
      toast.success('Day center schedule updated')
    }
    const onError = (cause: unknown): void => {
      toast.error(
        cause instanceof Error ? cause.message : 'Failed to update schedule'
      )
    }

    // Setter order matters: each prunes the ones after it against its own days.
    const apply = (next: DayCenterSchedule): void => {
      next.attendanceDays = formValues.days
      next.attendanceShifts = formValues.shifts
      next.preferredAppointmentDays = formValues.preferredDays
      next.preferredAppointmentShifts = formValues.preferredShifts
      next.note = formValues.note ?? undefined
    }

    if (schedule) {
      patchMutation.mutate(
        {
          instance: schedule,
          mutateFn: (next) => {
            apply(next)
          },
        },
        { onSuccess, onError }
      )
      return
    }

    const created = new DayCenterSchedule({
      participant: participant.reference,
    })
    apply(created)
    createMutation.mutate(created, { onSuccess, onError })
  })

  const renderDayToggles = (
    label: string,
    selectedDays: AttendanceWeekday[],
    offeredDays: readonly AttendanceWeekday[],
    onToggle: (next: AttendanceWeekday[]) => void
  ): JSX.Element => (
    // Both day rows label their toggles with the same weekday names, so the
    // legend is what tells the two groups apart for screen readers.
    <fieldset className="flex gap-2">
      <legend className="sr-only">{label}</legend>
      {offeredDays.map((day) => {
        const selected = selectedDays.includes(day)
        return (
          <Tooltip key={day}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant={selected ? 'default' : 'outline'}
                disabled={isReadOnly}
                aria-pressed={selected}
                aria-label={WEEKDAY_LONG_LABELS[day]}
                className="size-9 rounded-full text-xs"
                onClick={() => {
                  const next = selected
                    ? selectedDays.filter((d) => d !== day)
                    : [...selectedDays, day]
                  onToggle(ATTENDANCE_WEEKDAYS.filter((d) => next.includes(d)))
                }}
              >
                {WEEKDAY_LETTER_LABELS[day]}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{WEEKDAY_LONG_LABELS[day]}</TooltipContent>
          </Tooltip>
        )
      })}
    </fieldset>
  )

  const renderShiftSection = (
    label: string,
    mode: ShiftMode | null,
    modeDays: AttendanceWeekday[],
    currentShifts: AttendanceShiftsByDay,
    onModeChange: (mode: ShiftMode) => void,
    onDayShiftChange: (next: AttendanceShiftsByDay) => void
  ): JSX.Element => (
    <div className="flex w-full flex-col gap-3">
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="flex w-full items-start gap-6">
        <SegmentedControl
          options={SHIFT_MODE_OPTIONS}
          value={mode ?? undefined}
          onChange={onModeChange}
          disabled={isReadOnly}
          label={label}
          className="w-[301px] shrink-0"
        />
        {mode === 'custom' && modeDays.length > 0 && (
          <>
            <div className="w-px self-stretch bg-border" />
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              {modeDays.map((day) => (
                <div
                  key={day}
                  className="flex w-full items-center justify-between gap-2"
                >
                  <span className="text-xs">{WEEKDAY_LONG_LABELS[day]}</span>
                  <SegmentedControl
                    options={SHIFT_OPTIONS}
                    value={currentShifts[day]}
                    onChange={(shift) =>
                      onDayShiftChange({ ...currentShifts, [day]: shift })
                    }
                    disabled={isReadOnly}
                    label={`${WEEKDAY_LONG_LABELS[day]} ${label}`}
                    className="h-6 w-[106px] shrink-0"
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay className="max-w-[700px]">
        <DialogHeader className="flex-col gap-1">
          <div className="flex items-center gap-2">
            <DialogTitle>Day Center Schedule</DialogTitle>
            {schedule && <DevMedplumAppLinkOverlay to={schedule} />}
          </div>
          <DialogDescription>Recurring weekly attendance</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="schedule">
          <div className="px-6">
            <TabsList variant="underline" className="w-full justify-start">
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
              <TabsTrigger value="history">
                History{activity.length > 0 && ` · ${activity.length}`}
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="schedule">
            <Form onSubmit={submit}>
              <DialogContent className="gap-4">
                <div className="flex w-full flex-col gap-3">
                  <div className="flex w-full items-center justify-between">
                    <Label className="text-xs font-semibold">
                      Day center days
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {dayCenterDaysSummary(days, shiftMode ?? undefined)}
                    </span>
                  </div>
                  {renderDayToggles(
                    'Day center days',
                    days,
                    ATTENDANCE_WEEKDAYS,
                    setDays
                  )}
                </div>
                {renderShiftSection(
                  'Day center shift',
                  shiftMode,
                  days,
                  shifts,
                  (mode) => {
                    setValue('shiftMode', mode, dirty)
                    setValue('shifts', shiftsForMode(mode, days, shifts), dirty)
                  },
                  (next) => setValue('shifts', next, dirty)
                )}
                <div className="h-px w-full bg-border" />
                <div className="flex w-full flex-col gap-3">
                  <div className="flex w-full items-center justify-between">
                    <Label className="text-xs font-semibold">
                      Preferred external appointment days
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {preferredDaysSummary(
                        preferredDays,
                        preferredShiftMode ?? undefined
                      )}
                    </span>
                  </div>
                  {renderDayToggles(
                    'Preferred external appointment days',
                    preferredDays,
                    ATTENDANCE_WEEKDAYS,
                    setPreferredDays
                  )}
                </div>
                {renderShiftSection(
                  'Preferred external appointment shift',
                  preferredShiftMode,
                  preferredDays,
                  preferredShifts,
                  (mode) => {
                    const targetDays =
                      mode && mode !== 'custom' && preferredDays.length === 0
                        ? [...ATTENDANCE_WEEKDAYS]
                        : preferredDays
                    setValue('preferredShiftMode', mode, dirty)
                    setValue('preferredDays', targetDays, dirty)
                    setValue(
                      'preferredShifts',
                      shiftsForMode(mode, targetDays, preferredShifts),
                      dirty
                    )
                  },
                  (next) => setValue('preferredShifts', next, dirty)
                )}
                <div className="h-px w-full bg-border" />
                <div className="flex flex-col gap-1">
                  <Label className="text-xs font-semibold">Notes</Label>
                  <Textarea
                    disabled={isReadOnly}
                    placeholder="Transportation arranged, family request, PCP recommendation..."
                    className="placeholder:text-foreground/50"
                    value={values.note ?? ''}
                    onChange={(note) => setValue('note', note, dirty)}
                  />
                  <span className="text-xs text-muted-foreground">
                    Saved on the schedule.
                  </span>
                </div>
              </DialogContent>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </DialogClose>
                <SubmitButton
                  submitting={isSaving}
                  disabled={isReadOnly || !isDirty}
                >
                  Save changes
                </SubmitButton>
              </DialogFooter>
            </Form>
          </TabsContent>
          <TabsContent value="history">
            <DialogContent>
              {activity.length > 0 ? (
                <ActivityList>{activity}</ActivityList>
              ) : (
                <span className="text-sm text-muted-foreground">
                  No changes recorded yet.
                </span>
              )}
            </DialogContent>
          </TabsContent>
        </Tabs>
      </DialogDisplay>
    </Dialog>
  )
}
